// Alt text and caption handling.
//
// An image's description and its caption are different things. The caption is
// displayed and names the figure; the alt text describes it and is never
// rendered visually. They live in separate fields — but content authored before
// `alt_text` existed embedded the description in the caption as `[alt: …]`, so
// both routes are resolved here.
//
// Extraction and stripping are ONE function on purpose. If they ever disagree,
// students either see a raw `[alt:` marker or lose the description entirely.

export interface ExtractedCaption {
  // The description found in the caption, or undefined when there is none.
  altText?: string;
  // What should actually be shown, with any marker removed.
  displayCaption: string;
}

// `[alt:` with tolerance for spacing and case. Content has been authored by
// hand and by scripts, so `[Alt :` should not slip through as visible text.
const MARKER_START = /\[\s*alt\s*:\s*/i;

/**
 * Finds the index just past the `]` that closes a marker opened at
 * `contentStart`, counting nested brackets.
 *
 * A balanced scan rather than a regex because neither greedy nor lazy matching
 * is right: lazy truncates `[alt: see figure [3] for detail]` at the inner
 * bracket, and greedy runs one marker into the next.
 *
 * Returns the string length when the marker is never closed — an unclosed
 * `[alt:` almost certainly means the author forgot the bracket, and leaving it
 * alone would put the marker on screen, which is the bug being fixed.
 */
function findMarkerEnd(caption: string, contentStart: number): number {
  let depth = 1;

  for (let index = contentStart; index < caption.length; index += 1) {
    const character = caption[index];

    if (character === '[') {
      depth += 1;
    } else if (character === ']') {
      depth -= 1;

      if (depth === 0) {
        return index;
      }
    }
  }

  return caption.length;
}

function tidyCaption(caption: string) {
  // Removing a marker from mid-caption leaves a double space, and removing one
  // from the end leaves a trailing space before the period the author wrote.
  return caption.replace(/[ \t]{2,}/g, ' ').replace(/\s+$/, '').trim();
}

/**
 * Splits a caption into the description it embeds and the text to display.
 *
 * Every marker is removed, and their descriptions are joined. Two markers is
 * almost certainly an authoring mistake, but taking only the first would either
 * leave the second visible or silently discard it — neither is better than
 * keeping both out of sight and in the description.
 */
export function extractAltFromCaption(
  caption: string | undefined,
): ExtractedCaption {
  if (!caption) {
    return { displayCaption: '' };
  }

  const descriptions: string[] = [];
  let remaining = caption;
  let result = '';

  for (;;) {
    const match = MARKER_START.exec(remaining);

    if (!match || match.index === undefined) {
      result += remaining;
      break;
    }

    const contentStart = match.index + match[0].length;
    const end = findMarkerEnd(remaining, contentStart);
    const description = remaining.slice(contentStart, end).trim();

    if (description.length > 0) {
      descriptions.push(description);
    }

    result += remaining.slice(0, match.index);
    // `end` is the closing bracket, or the length when unclosed; +1 is safe
    // either way because slice clamps.
    remaining = remaining.slice(end + 1);
  }

  return {
    altText: descriptions.length > 0 ? descriptions.join(' ') : undefined,
    displayCaption: tidyCaption(result),
  };
}

/**
 * The `alt` attribute for an image: stored alt text, then a legacy caption
 * marker, then empty.
 *
 * Empty is the final fallback deliberately. The old chain ended
 * `heading ?? caption ?? 'Course illustration'`, and both of those were removed:
 * a heading is already announced in its own right, and a generic placeholder
 * withholds every fact about the image while making it look described to an
 * automated check. An undescribed image with a visible caption is decorative,
 * and its `<figcaption>` is its text alternative.
 */
export function resolveImageAltText({
  altText,
  caption,
}: {
  altText?: string;
  caption?: string;
}): string {
  const stored = altText?.trim();

  if (stored) {
    return stored;
  }

  return extractAltFromCaption(caption).altText ?? '';
}

// A closing tag, or a line break. Captions are historically PLAIN TEXT, so
// treating every one as markup would destroy the ones containing a comparison:
// measured, `<` before a space or a digit survives the HTML parser untouched
// (`T < 5 °C`, `5 < x < 10`), but `<` before a letter opens a tag that swallows
// the rest — `Ratio A<B` renders as `Ratio A`. Requiring a closing tag keeps
// that literal while letting a deliberately formatted caption through.
const MARKUP_PATTERN = /<\/[a-z][a-z0-9]*\s*>|<br\s*\/?>/i;

export function shouldRenderCaptionAsHtml(caption: string | undefined): boolean {
  return Boolean(caption && MARKUP_PATTERN.test(caption));
}
